import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {PlusIcon, ChevronUpIcon, ChevronDownIcon, PencilSquareIcon, CheckIcon} from "@heroicons/react/24/outline";
import useTranslate from "../../core/languages/hooks/useTranslate";
import usePaymentCardsGet from "../hooks/usePaymentCardsGet";
import usePaymentCardGet from "../hooks/usePaymentCardGet";
import CardBrandLogo from "./CardBrandLogo";
import CreditCardPaycometForm from "./CreditCardPaycometForm";
import Loader from "../../ui/shared/Loader/Loader";
import {PaymentCardResult} from "../types/PaymentCard";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PaymentCardSelectorProps {
    /** Currently selected card UUID */
    value: string | null;
    /** Called whenever selection changes */
    onChange: (cardUuid: string | null) => void;
    /** Whether the user is authenticated. When false, skip saved-cards fetch and
     *  only show the add-card form; the uuid from the save response is propagated
     *  directly via onChange so it can be attached to the pre-booking. */
    authenticated?: boolean;
    /** UUID of the card already assigned to the booking/prebooking.
     *  Takes priority over the primary card during auto-selection. */
    preferredCardUuid?: string | null;
}

// ─── Dropdown card option ─────────────────────────────────────────────────────

const CardDropdownOption: React.FC<{
    brand: string;
    pan: string;
    cardHolder: string;
    expDate: string;
    selected: boolean;
    onClick: () => void;
}> = ({brand, pan, cardHolder, expDate, selected, onClick}) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800 ${
            selected ? "bg-neutral-100 dark:bg-neutral-700" : ""
        }`}
    >
        <div className="flex-shrink-0 flex items-center justify-center rounded-lg bg-neutral-100 dark:bg-neutral-700 p-1">
            <CardBrandLogo brand={brand}/>
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{pan}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">{cardHolder} · {expDate}</p>
        </div>
        {selected && <CheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0"/>}
    </button>
);

// ─── Added card display (unauthenticated, read-only) ──────────────────────────

const AddedCardDisplay: React.FC<{
    cardData: PaymentCardResult;
    onChangeCard: () => void;
}> = ({cardData, onChangeCard}) => {
    const t = useTranslate();

    return (
        <div className="space-y-3">
            {/* Card info row - Similar to dropdown style */}
            <div className="flex items-center gap-3 p-4 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors">
                {/* Brand logo */}
                <div className="flex flex-shrink-0 items-center justify-center rounded-lg bg-neutral-100 dark:bg-neutral-700 p-1">
                    <CardBrandLogo brand={cardData.brand ?? ""}/>
                </div>

                {/* Card info */}
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {cardData.pan ?? "····"}
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                        {cardData.cardHolder ?? ""}{cardData.expDate ? ` · ${cardData.expDate}` : ""}
                    </p>
                </div>
            </div>

            {/* Change card button */}
            <button
                type="button"
                onClick={onChangeCard}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-neutral-300 dark:border-neutral-700 px-4 py-3 text-sm font-medium text-neutral-500 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-colors"
            >
                <PencilSquareIcon className="h-4 w-4"/>
                {t("paymentCard.changeCard")}
            </button>
        </div>
    );
};

// ─── Unauthenticated view: add-card form or added card display ────────────────

const UnauthenticatedCardSection: React.FC<{
    value: string | null;
    onChange: (uuid: string | null) => void;
    cardData: PaymentCardResult | null;
    onCardSaved: (result: PaymentCardResult) => void;
    onChangeCard: () => void;
    isChangingCard: boolean;
    setIsChangingCard: (b: boolean) => void;
}> = ({value, onChange, cardData, onCardSaved, onChangeCard, isChangingCard, setIsChangingCard}) => {
    const t = useTranslate();
    const [formKey, setFormKey] = useState(0);

    // If a card is already associated (uuid set) and we have its data AND not changing → show it
    if (value && cardData && !isChangingCard) {
        return <AddedCardDisplay cardData={cardData} onChangeCard={() => {
            onChangeCard();
            setIsChangingCard(true);
            setFormKey(prev => prev + 1);
        }}/>;
    }

    // If uuid is set but we don't have card data (e.g. came from parent state) AND not changing
    if (value && !cardData && !isChangingCard) {
        return (
            <div className="space-y-3">
                <div className="flex items-center gap-3 p-4 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        ✓ {t("paymentCard.cardAdded")}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        onChangeCard();
                        setIsChangingCard(true);
                        setFormKey(prev => prev + 1);
                    }}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-neutral-300 dark:border-neutral-700 px-4 py-3 text-sm font-medium text-neutral-500 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-colors"
                >
                    <PencilSquareIcon className="h-4 w-4"/>
                    {t("paymentCard.changeCard")}
                </button>
            </div>
        );
    }

    // No card yet OR changing card → show form
    return (
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-700 p-5 bg-white dark:bg-neutral-900">
            <CreditCardPaycometForm
                key={`card-form-${formKey}`}
                submitLabel={t("paymentCard.saveAndSelect")}
                onSuccess={(result) => {
                    if (result.uuid) {
                        onChange(result.uuid);
                        onCardSaved(result);
                        setIsChangingCard(false);
                    }
                }}
            />
            {isChangingCard && (
                <button
                    type="button"
                    onClick={() => {
                        setIsChangingCard(false);
                        if (cardData) onChange(cardData.uuid ?? null);
                    }}
                    className="mt-3 w-full text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
                >
                    {t("paymentCard.cancelAddCard")}
                </button>
            )}
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const PaymentCardSelector: React.FC<PaymentCardSelectorProps> = ({value, onChange, authenticated = true, preferredCardUuid}) => {
    const t = useTranslate();
    const {data: cards, isLoading} = usePaymentCardsGet({execute: authenticated});
    const [showAddForm, setShowAddForm] = useState(false);
    const [addFormKey, setAddFormKey] = useState(0);

    // Stores card data returned by the backend after saving (unauthenticated flow)
    const [unauthCardData, setUnauthCardData] = useState<PaymentCardResult | null>(null);

    // Track if user is in the process of changing their card (unauthenticated flow)
    const [isChangingCard, setIsChangingCard] = useState(false);

    // Fetch existing card data for unauthenticated users who already have a card linked
    const {data: existingCard} = usePaymentCardGet(preferredCardUuid, {
        execute: !authenticated && !!preferredCardUuid && !unauthCardData,
    });

    // When the existing card data arrives, populate the display and propagate the UUID
    useEffect(() => {
        if (!existingCard) return;
        setUnauthCardData({
            idUser: '',
            tokenUser: '',
            uuid: existingCard.uuid,
            brand: existingCard.brand,
            pan: existingCard.pan,
            cardHolder: existingCard.cardHolder,
            expDate: existingCard.expDate,
        });
        if (!value) onChange(existingCard.uuid);
    }, [existingCard]);

    // Track previous card UUIDs to detect newly added cards
    const prevUuidsRef = useRef<Set<string>>(new Set());

    // ── Authenticated dropdown state (must be declared unconditionally) ────────
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    // Calculate and update portal position from trigger button
    const updateDropdownPosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownStyle({
            position: 'fixed',
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
        });
    }, []);

    // Reposition on scroll/resize while open
    useEffect(() => {
        if (!dropdownOpen) return;
        updateDropdownPosition();
        window.addEventListener('scroll', updateDropdownPosition, true);
        window.addEventListener('resize', updateDropdownPosition);
        return () => {
            window.removeEventListener('scroll', updateDropdownPosition, true);
            window.removeEventListener('resize', updateDropdownPosition);
        };
    }, [dropdownOpen, updateDropdownPosition]);

    // Update position synchronously on open to avoid flicker
    useLayoutEffect(() => {
        if (dropdownOpen) updateDropdownPosition();
    }, [dropdownOpen, updateDropdownPosition]);

    // Auto-select card on initial load (authenticated only).
    // Priority: 1) newly added card  2) preferredCardUuid (booking's card)  3) primary card  4) first card
    useEffect(() => {
        if (!authenticated || !cards || cards.length === 0) return;

        const currentUuids = new Set(cards.map(c => c.uuid));

        // Find newly added card (present now but not before)
        const newUuid = cards.find(c => !prevUuidsRef.current.has(c.uuid))?.uuid;

        if (newUuid && prevUuidsRef.current.size > 0) {
            // A card was just added — auto-select it and close form
            onChange(newUuid);
            setShowAddForm(false);
            setDropdownOpen(false);
        } else if (!value) {
            // Initial load — prefer booking's card, then primary, then first
            const preferred = preferredCardUuid ? cards.find(c => c.uuid === preferredCardUuid) : null;
            const primary = cards.find(c => c.principal);
            onChange(preferred?.uuid ?? primary?.uuid ?? cards[0].uuid);
        }

        prevUuidsRef.current = currentUuids;
    }, [cards, authenticated]);

    // Close dropdown on outside click (checks both trigger and portal panel)
    useEffect(() => {
        if (!authenticated) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            const insideTrigger = triggerRef.current?.contains(target);
            const insidePanel = panelRef.current?.contains(target);
            if (!insideTrigger && !insidePanel) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [authenticated]);

    // ── Unauthenticated: card form / added card display ───────────────────────
    if (!authenticated) {
        return (
            <UnauthenticatedCardSection
                value={value}
                onChange={onChange}
                cardData={unauthCardData}
                onCardSaved={(result) => setUnauthCardData(result)}
                onChangeCard={() => {
                    onChange(null);
                    setUnauthCardData(null);
                }}
                isChangingCard={isChangingCard}
                setIsChangingCard={setIsChangingCard}
            />
        );
    }

    // ── Authenticated ─────────────────────────────────────────────────────────

    if (isLoading) return <Loader centerHorizontally/>;

    const selectedCard = cards?.find(c => c.uuid === value);

    // ── Portal dropdown: renderizado en document.body para escapar overflow-hidden ──
    const dropdownPanel = dropdownOpen ? ReactDOM.createPortal(
        <div
            ref={panelRef}
            style={dropdownStyle}
            className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl overflow-hidden"
        >
            {/* Lista de tarjetas con scroll interno si la lista es grande */}
            {cards && cards.length > 0 && (
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800 max-h-64 overflow-y-auto">
                    {cards.map(card => (
                        <CardDropdownOption
                            key={card.uuid}
                            brand={card.brand}
                            pan={card.pan}
                            cardHolder={card.cardHolder}
                            expDate={card.expDate}
                            selected={value === card.uuid}
                            onClick={() => {
                                onChange(card.uuid);
                                setDropdownOpen(false);
                                setShowAddForm(false);
                            }}
                        />
                    ))}
                </div>
            )}

            {/* Botón añadir nueva tarjeta */}
            <div className="border-t border-neutral-100 dark:border-neutral-800">
                <button
                    type="button"
                    onClick={() => {
                        setShowAddForm(prev => {
                            const newValue = !prev;
                            if (newValue) {
                                // Si estamos abriendo el formulario, incrementar key
                                setAddFormKey(k => k + 1);
                            }
                            return newValue;
                        });
                    }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                    {showAddForm
                        ? <><ChevronUpIcon className="h-4 w-4"/>{t("paymentCard.cancelAddCard")}</>
                        : <><PlusIcon className="h-4 w-4"/>{t("paymentCard.addCard")}</>
                    }
                </button>
            </div>

            {/* Formulario inline para añadir tarjeta */}
            {showAddForm && (
                <div className="border-t border-neutral-100 dark:border-neutral-800 p-4 bg-neutral-50 dark:bg-neutral-800/50">
                    <CreditCardPaycometForm
                        key={`add-form-${addFormKey}`}
                        submitLabel={t("paymentCard.saveAndSelect")}
                        onSuccess={() => {
                            setShowAddForm(false);
                        }}
                    />
                </div>
            )}
        </div>,
        document.body
    ) : null;

    return (
        <div className="space-y-3">
            {/* Trigger del dropdown */}
            <div className="relative">
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={() => setDropdownOpen(prev => !prev)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border transition-colors bg-white dark:bg-neutral-900 ${
                        dropdownOpen
                            ? "border-primary-400 dark:border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800"
                            : "border-neutral-200 dark:border-neutral-700 hover:border-primary-300 dark:hover:border-primary-600"
                    }`}
                >
                    {selectedCard ? (
                        <>
                            <div className="flex-shrink-0 flex items-center justify-center rounded-lg bg-neutral-100 dark:bg-neutral-700 p-1">
                                <CardBrandLogo brand={selectedCard.brand}/>
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{selectedCard.pan}</p>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400">{selectedCard.cardHolder} · {selectedCard.expDate}</p>
                            </div>
                        </>
                    ) : (
                        <span className="flex-1 text-left text-sm text-neutral-400 dark:text-neutral-500">
                            {cards && cards.length > 0
                                ? t("paymentCard.selectCard")
                                : t("paymentCard.noSavedCards")}
                        </span>
                    )}
                    {dropdownOpen
                        ? <ChevronUpIcon className="h-4 w-4 text-neutral-400 flex-shrink-0"/>
                        : <ChevronDownIcon className="h-4 w-4 text-neutral-400 flex-shrink-0"/>
                    }
                </button>

                {/* Portal: el dropdown se monta en document.body, libre de overflow-hidden */}
                {dropdownPanel}
            </div>

            {/* Aviso de validación cuando no hay tarjeta seleccionada */}
            {!value && !isLoading && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                    {t("paymentCard.mustSelectCard")}
                </p>
            )}
        </div>
    );
};

export default PaymentCardSelector;
